import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Languages } from "lucide-react";

const HERO_IMG = "/eve-portrait.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <div className="container-edge pt-16 md:pt-24 lg:pt-28 pb-20 md:pb-28 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <div className="lg:col-span-7">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={0}
              className="eyebrow inline-flex items-center gap-2"
              data-testid="hero-eyebrow"
            >
              <Languages size={14} className="opacity-80" />
              Spanish · English Interpreting
            </motion.div>

            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={1}
              className="mt-6 font-display text-[hsl(var(--ink))] text-4xl sm:text-5xl lg:text-[5.25rem] leading-[1.02] tracking-tight"
              data-testid="hero-heading"
            >
              Every word matters.
              <span className="block italic text-[hsl(var(--accent))]">
                Every patient. Every case.
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-7 max-w-xl text-[hsl(var(--text-muted))] text-base md:text-lg leading-relaxed"
              data-testid="hero-subheading"
            >
              Led by <span className="text-[hsl(var(--ink))] font-medium">Eve Gomez</span>,
              our team of qualified Spanish &amp; English interpreters supports
              medical, legal, immigration, and business clients with clarity,
              care, and complete confidentiality.
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a href="#contact" data-testid="hero-cta-primary" className="btn-primary">
                Request a Quote <ArrowRight size={16} />
              </a>
              <a href="#services" data-testid="hero-cta-secondary" className="btn-secondary">
                Explore Services
              </a>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-12 flex items-center gap-6 text-xs uppercase tracking-[0.2em] text-[hsl(var(--text-muted))]"
            >
              <span data-testid="hero-trust-confidential">Confidential</span>
              <span className="w-1 h-1 rounded-full bg-[hsl(var(--text-muted))]/40" />
              <span data-testid="hero-trust-bilingual">Native Bilingual</span>
              <span className="w-1 h-1 rounded-full bg-[hsl(var(--text-muted))]/40" />
              <span data-testid="hero-trust-onsite">On-site &amp; Remote</span>
            </motion.div>
          </div>

          {/* Right image */}
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative w-full overflow-hidden rounded-sm aspect-[4/5]">
              <img
                src={HERO_IMG}
                alt="Eve Gomez, founder of EG Spanish Interpreting"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-black/10" />
            </div>

            {/* Floating credential card */}
            <div
              data-testid="hero-credential-card"
              className="hidden md:block absolute -left-10 bottom-10 bg-[hsl(var(--surface))] border border-[hsl(var(--line))] shadow-sm p-6 max-w-[260px]"
            >
              <div className="eyebrow">A lifetime of practice</div>
              <p className="mt-3 font-display text-2xl text-[hsl(var(--ink))] leading-tight">
                Trusted across hospitals, courts &amp; boardrooms.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative line */}
      <div className="absolute left-0 right-0 bottom-0 h-px bg-[hsl(var(--line))]" />
    </section>
  );
}
